import { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

export interface SeitenkopfProps
  extends Omit<ComponentPropsWithoutRef<'header'>, 'title'> {
  /** Application name, links to the home page. */
  title: ReactNode;
  /** Optional addition, e.g. the operating organisation. */
  subtitle?: ReactNode;
  /** Target of the title link. */
  homeHref?: string;
  /** Element for the title link, e.g. react-router's `Link` inside a router. */
  linkAs?: ElementType;
  /** Actions on the right-hand side (search, notifications, settings). */
  actions?: ReactNode;
  /**
   * Logo shown instead of title and subtitle. The title stays the link's accessible
   * name for screen readers; `alt` names the logo itself, e.g. the organisation.
   */
  logo?: { src: string; alt: string };
}

/**
 * Application header.
 *
 * Not to be confused with KERN's `Kopfzeile`, the narrow identification banner
 * ("Offizielle Website ..."). This header carries the application name and tools. KERN has
 * no component for it, so the design follows the KERN tokens.
 */
export const Seitenkopf = ({
  title,
  subtitle,
  homeHref = '/',
  linkAs: Link = 'a',
  actions,
  logo,
  className,
  ...props
}: SeitenkopfProps) => (
  <header
    {...props}
    className={['vf-seitenkopf', className].filter(Boolean).join(' ')}
  >
    <div className="vf-seitenkopf__brand">
      <Link
        {...(Link === 'a' ? { href: homeHref } : { to: homeHref })}
        className="vf-seitenkopf__title"
      >
        {logo ? (
          <>
            <img
              className="vf-seitenkopf__logo"
              src={logo.src}
              alt={logo.alt}
            />
            {/* Keeps logo text and title apart in the link's accessible name. */}{' '}
            <span className="kern-sr-only">{title}</span>
          </>
        ) : (
          title
        )}
      </Link>
      {subtitle && !logo && (
        <span className="kern-body kern-body--small kern-body--muted">
          {subtitle}
        </span>
      )}
    </div>
    {actions && <div className="vf-seitenkopf__actions">{actions}</div>}
  </header>
);
